import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Link2,
  MousePointerClick,
  Globe2,
  TrendingUp,
  Plus,
  ArrowUpRight,
  Activity,
  Sparkles,
  Monitor,
  Smartphone,
  Tablet,
  Clock,
  MapPin,
  Share2,
  Chrome,
  MousePointer2,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import { Link as LinkType, AnalyticsSummary } from "../../../types";
import HeroSection from "../../../components/ui/HeroSection";
import RichStatCard from "../../../components/ui/RichStatCard";
import Badge from "../../../components/ui/Badge";
import Button from "../../../components/ui/Button";
import EmptyState from "../../../components/ui/EmptyState";
import WorldMap from "../../../components/ui/WorldMap";
import HelpHint from "../../../components/ui/HelpHint";
import Skeleton, {
  SkeletonStat,
} from "../../../components/ui/Skeleton";
import { formatDateTime, formatNumber } from "../../../utils";

const DAYS = 14;

const dayKey = (d: Date) => d.toISOString().slice(0, 10);

// Zero-fill the server's per-day counts ([{date:'YYYY-MM-DD', clicks}]) into a
// continuous trailing `days`-day window, labelled 'MM-DD' for the chart.
const fillDailyWindow = (
  daily: { date: string; clicks: number }[],
  days: number,
) => {
  const counts = new Map(daily.map((d) => [d.date, d.clicks]));
  const out: { date: string; clicks: number }[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = dayKey(d);
    out.push({ date: key.slice(5), clicks: counts.get(key) || 0 });
  }
  return out;
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.[0]) return null;
  return (
    <div className="bg-ink-900 text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-ink-400 mb-0.5 text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="font-semibold font-display text-base">
        {payload[0].value}{" "}
        <span className="text-xs font-normal text-ink-400">
          {payload[0].value === 1 ? "click" : "clicks"}
        </span>
      </div>
    </div>
  );
};

const deviceIconFor = (type?: string) => {
  if (type === "mobile") return <Smartphone size={12} />;
  if (type === "tablet") return <Tablet size={12} />;
  return <Monitor size={12} />;
};

const DEVICE_COLORS: Record<string, string> = {
  desktop: "#6366f1",
  mobile: "#10b981",
  tablet: "#f59e0b",
};
const deviceColor = (name: string) => DEVICE_COLORS[name] || "#a1a1aa";

// Reusable section card matching the dashboard's existing panels.
const Panel = ({
  title,
  subtitle,
  action,
  help,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  help?: { anchor?: string; body: React.ReactNode };
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden ${className}`}
  >
    <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
      <div>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-ink-900 tracking-tight">
          {title}
          {help && (
            <HelpHint title={title} anchor={help.anchor}>
              {help.body}
            </HelpHint>
          )}
        </h3>
        {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    <div className="px-5 pb-5 pt-2">{children}</div>
  </div>
);

const BreakdownList = ({
  items,
  total,
  emptyLabel,
  accent = "brand",
  icon,
}: {
  items: { name: string; value: number }[];
  total: number;
  emptyLabel: string;
  accent?: "brand" | "emerald" | "violet" | "amber";
  icon?: (name: string) => React.ReactNode;
}) => {
  const gradients: Record<string, string> = {
    brand: "from-brand-500 to-violet-500",
    emerald: "from-emerald-500 to-teal-500",
    violet: "from-violet-500 to-fuchsia-500",
    amber: "from-amber-500 to-orange-500",
  };
  if (items.length === 0) {
    return <p className="text-sm text-ink-400 text-center py-6">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = total > 0 ? (item.value / total) * 100 : 0;
        return (
          <div key={item.name}>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="flex items-center gap-1.5 text-ink-800 truncate pr-2 capitalize font-medium">
                {icon && <span className="text-ink-400">{icon(item.name)}</span>}
                {item.name}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-ink-400 text-xs tabular-nums">
                  {pct.toFixed(0)}%
                </span>
                <span className="text-ink-900 font-semibold text-xs tabular-nums w-7 text-right">
                  {item.value}
                </span>
              </div>
            </div>
            <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${gradients[accent]} rounded-full transition-all duration-700`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Tooltip for category charts (pie/donut) — keyed on the slice name.
const CategoryTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  return (
    <div className="bg-ink-900 text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-ink-400 mb-0.5 text-[10px] uppercase tracking-wide capitalize">
        {payload[0].name}
      </div>
      <div className="font-semibold font-display text-base">
        {payload[0].value}{" "}
        <span className="text-xs font-normal text-ink-400">
          {payload[0].value === 1 ? "click" : "clicks"}
        </span>
      </div>
    </div>
  );
};

// Tooltip for the hour-of-day bar chart (label is a "13:00"-style string).
const HourTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.[0]) return null;
  return (
    <div className="bg-ink-900 text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-ink-400 mb-0.5 text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="font-semibold font-display text-base">
        {payload[0].value}{" "}
        <span className="text-xs font-normal text-ink-400">
          {payload[0].value === 1 ? "click" : "clicks"}
        </span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const services = useAppServices();
  const { buildPath } = useMarketplace();
  const [links, setLinks] = useState<LinkType[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(
    async (showLoading = false) => {
      if (showLoading) setLoading(true);
      // Bot exclusion + retention windowing now happen server-side; the daily
      // chart window (DAYS) is passed through for the time-series facet.
      const [linksRes, summaryRes] = await Promise.all([
        services.url_tracker.get(),
        services.url_tracker_logs.summary({ query: `days=${DAYS}` }),
      ]);
      if (Array.isArray(linksRes.response)) setLinks(linksRes.response);
      if (summaryRes.response) setSummary(summaryRes.response as AnalyticsSummary);
      if (showLoading) setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Total tracked (non-bot, retention-bounded) clicks — used as the denominator
  // for breakdown percentages and the "tracked" count.
  const total = summary?.total ?? 0;

  const linkTitleById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const l of links) m[l._id] = l.title || "Untitled";
    return m;
  }, [links]);

  const stats = useMemo(() => {
    const totalLinks = links.length;
    const totalClicks = links.reduce((s, l) => s + (l.clicks || 0), 0);
    const avgClicks =
      totalLinks > 0 ? Math.round(totalClicks / totalLinks) : 0;
    const uniqueCountries = summary?.countries.length ?? 0;
    return { totalLinks, totalClicks, avgClicks, uniqueCountries };
  }, [links, summary]);

  const dailySeries = useMemo(
    () => fillDailyWindow(summary?.daily ?? [], DAYS),
    [summary],
  );
  const clickSparkData = dailySeries.map((d) => d.clicks);
  const linkSparkData = useMemo(() => {
    // synthesize per-day created counts from links list
    const map = new Map<string, number>();
    const now = new Date();
    for (let i = DAYS - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      map.set(dayKey(d), 0);
    }
    for (const l of links) {
      const k = l.createdAt.slice(0, 10);
      if (map.has(k)) map.set(k, (map.get(k) || 0) + 1);
    }
    return Array.from(map.values());
  }, [links]);

  const topLinks = useMemo(
    () =>
      [...links]
        .sort((a, b) => (b.clicks || 0) - (a.clicks || 0))
        .slice(0, 5),
    [links],
  );

  // All breakdowns come pre-aggregated + sorted from the server; we just slice
  // to the count each panel renders.
  const topCountries = useMemo(
    () => (summary?.countries ?? []).slice(0, 5),
    [summary],
  );
  const deviceData = useMemo(
    () => (summary?.devices ?? []).slice(0, 3),
    [summary],
  );
  const browsers = useMemo(() => (summary?.browsers ?? []).slice(0, 6), [summary]);
  const sources = useMemo(() => (summary?.sources ?? []).slice(0, 6), [summary]);
  const cities = useMemo(() => (summary?.cities ?? []).slice(0, 6), [summary]);

  // Clicks by hour of day (UTC hour from the server). Zero-filled to 24 bars.
  const hourly = useMemo(() => {
    const arr = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: `${h.toString().padStart(2, "0")}:00`,
      clicks: 0,
    }));
    for (const { hour, value } of summary?.hourly ?? []) {
      if (arr[hour]) arr[hour].clicks = value;
    }
    return arr;
  }, [summary]);

  // Country entries (with code) for the world map — already sorted by the server.
  const countryEntries = useMemo(
    () => summary?.countries ?? [],
    [summary],
  );

  const peakHour = useMemo(() => {
    if (total === 0) return null;
    return hourly.reduce((a, b) => (b.clicks > a.clicks ? b : a));
  }, [hourly, total]);

  const recentLogs = summary?.recent ?? [];

  return (
    <div className="space-y-6">
      <HeroSection
        eyebrow={
          <>
            <Sparkles size={11} className="text-brand-500" />
            <span>Welcome back</span>
          </>
        }
        title={
          <>
            Your links,{" "}
            <span className="bg-brand-gradient bg-clip-text text-transparent">
              measured
            </span>
            .
          </>
        }
        description="Track clicks across countries, cities and devices — in real time, with zero third-party requests."
        action={
          <RouterLink to={buildPath("links?new=1")}>
            <Button
              variant="gradient"
              size="lg"
              icon={<Plus size={15} strokeWidth={2.5} />}
            >
              Create new link
            </Button>
          </RouterLink>
        }
      />

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          <>
            <RichStatCard
              label="Total clicks"
              value={stats.totalClicks}
              data={clickSparkData}
              tone="brand"
              icon={<MousePointerClick size={16} strokeWidth={2.25} />}
            />
            <RichStatCard
              label="Total links"
              value={stats.totalLinks}
              data={linkSparkData}
              tone="violet"
              icon={<Link2 size={16} strokeWidth={2.25} />}
            />
            <RichStatCard
              label="Countries"
              value={stats.uniqueCountries}
              data={clickSparkData}
              tone="emerald"
              icon={<Globe2 size={16} strokeWidth={2.25} />}
            />
            <RichStatCard
              label="Avg clicks / link"
              value={stats.avgClicks}
              data={clickSparkData}
              tone="amber"
              icon={<TrendingUp size={16} strokeWidth={2.25} />}
            />
          </>
        )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Activity chart spans 2 cols */}
        <div className="lg:col-span-2 bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 pb-2 gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink-900 tracking-tight">
                Activity over time
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Clicks per day across all your links — last {DAYS} days.
              </p>
            </div>
            <Badge tone="emerald" dot>
              Live
            </Badge>
          </div>
          <div className="px-3 pb-4 h-56">
            {loading ? (
              <div className="px-2 pt-3">
                <Skeleton className="h-44 w-full" rounded="rounded-xl" />
              </div>
            ) : total === 0 ? (
              <EmptyState
                icon={<Activity size={22} />}
                tone="brand"
                title="No activity yet"
                description="Share a short link and we'll plot clicks here."
              />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={dailySeries}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="dashClickGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                      <stop
                        offset="100%"
                        stopColor="#6366f1"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    stroke="#f4f4f5"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    stroke="#a1a1aa"
                    tickLine={false}
                    axisLine={false}
                    dy={4}
                  />
                  <YAxis
                    fontSize={11}
                    stroke="#a1a1aa"
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={28}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{
                      stroke: "#a1a1aa",
                      strokeDasharray: "3 3",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="clicks"
                    stroke="#6366f1"
                    strokeWidth={2.25}
                    fill="url(#dashClickGrad)"
                    animationDuration={600}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Top countries */}
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 pb-2 gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink-900 tracking-tight">
                Top countries
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Where your clicks come from
              </p>
            </div>
          </div>
          <div className="p-5 pt-3">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : topCountries.length === 0 ? (
              <p className="text-sm text-ink-400 text-center py-6">
                No geo data yet
              </p>
            ) : (
              <div className="space-y-3">
                {topCountries.map((c) => {
                  const pct =
                    stats.totalClicks > 0
                      ? (c.value / stats.totalClicks) * 100
                      : 0;
                  return (
                    <div key={c.name}>
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="text-ink-800 font-medium truncate pr-2">
                          {c.name}
                        </span>
                        <span className="text-ink-500 text-xs tabular-nums">
                          {c.value} · {pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-gradient rounded-full transition-all duration-700"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Breakdowns: devices · browsers · sources */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Devices" subtitle="Desktop vs mobile vs tablet">
          {loading ? (
            <Skeleton className="h-32 w-full" rounded="rounded-xl" />
          ) : deviceData.length === 0 ? (
            <p className="text-sm text-ink-400 text-center py-6">
              No device data yet
            </p>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-32 h-32 shrink-0 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deviceData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={38}
                      outerRadius={58}
                      paddingAngle={2}
                      stroke="none"
                      animationDuration={600}
                    >
                      {deviceData.map((d) => (
                        <Cell key={d.name} fill={deviceColor(d.name)} />
                      ))}
                    </Pie>
                    <Tooltip content={<CategoryTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="font-display font-bold text-ink-900 text-lg leading-none tabular-nums">
                    {formatNumber(total)}
                  </span>
                  <span className="text-[10px] uppercase tracking-wide text-ink-400">
                    clicks
                  </span>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                {deviceData.map((d) => {
                  const pct = total
                    ? (d.value / total) * 100
                    : 0;
                  return (
                    <div key={d.name} className="flex items-center gap-2 text-sm">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: deviceColor(d.name) }}
                      />
                      <span className="flex items-center gap-1 text-ink-700 capitalize flex-1 min-w-0">
                        {deviceIconFor(d.name)}
                        <span className="truncate">{d.name}</span>
                      </span>
                      <span className="text-ink-900 font-semibold tabular-nums text-xs">
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Top browsers" subtitle="Clients used to open links">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : (
            <BreakdownList
              items={browsers}
              total={total}
              emptyLabel="No browser data yet"
              accent="violet"
              icon={() => <Chrome size={11} />}
            />
          )}
        </Panel>

        <Panel title="Traffic sources" subtitle="Where clicks originate">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : (
            <BreakdownList
              items={sources}
              total={total}
              emptyLabel="No source data yet"
              accent="emerald"
              icon={() => <Share2 size={11} />}
            />
          )}
        </Panel>
      </section>

      {/* Clicks by hour + Top cities */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel
          className="lg:col-span-2"
          title="Clicks by hour of day"
          help={{
            anchor: "analytics-time",
            body: "When your audience is most active across all links — use it to time your sends. Hours are bucketed in UTC.",
          }}
          subtitle="When your audience is most active"
          action={
            peakHour && peakHour.clicks > 0 ? (
              <Badge tone="amber">
                <Clock size={11} /> Peak {peakHour.label}
              </Badge>
            ) : undefined
          }
        >
          {loading ? (
            <Skeleton className="h-48 w-full" rounded="rounded-xl" />
          ) : total === 0 ? (
            <EmptyState
              icon={<Clock size={22} />}
              tone="brand"
              title="No activity yet"
              description="Once clicks roll in, you'll see your busiest hours here."
            />
          ) : (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={hourly}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid stroke="#f4f4f5" vertical={false} />
                  <XAxis
                    dataKey="label"
                    fontSize={10}
                    stroke="#a1a1aa"
                    tickLine={false}
                    axisLine={false}
                    interval={2}
                    dy={4}
                  />
                  <YAxis
                    fontSize={11}
                    stroke="#a1a1aa"
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={28}
                  />
                  <Tooltip
                    content={<HourTooltip />}
                    cursor={{ fill: "#f4f4f5" }}
                  />
                  <Bar
                    dataKey="clicks"
                    fill="#6366f1"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={18}
                    animationDuration={600}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Top cities" subtitle="Most active locations">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : (
            <BreakdownList
              items={cities}
              total={total}
              emptyLabel="No city data yet"
              accent="amber"
              icon={() => <MapPin size={11} />}
            />
          )}
        </Panel>
      </section>

      {/* World map */}
      <Panel
        title="Clicks by country"
        help={{
          anchor: "analytics-map",
          body: "Geographic spread of clicks across all your links, shaded by volume. Location comes from offline GeoIP data — privacy-friendly, no third-party tracking.",
        }}
        subtitle="Geographic distribution across all your links"
        action={
          <span className="inline-flex items-center gap-1 text-xs text-ink-500">
            <MousePointer2 size={12} />
            {formatNumber(total)} tracked
          </span>
        }
      >
        {loading ? (
          <Skeleton className="h-64 w-full" rounded="rounded-xl" />
        ) : (
          <WorldMap entries={countryEntries} />
        )}
      </Panel>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top links */}
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 pb-2 gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink-900 tracking-tight">
                Top performing
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Most-clicked links across all time.
              </p>
            </div>
            <RouterLink
              to={buildPath("links")}
              className="inline-flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-900"
            >
              View all <ArrowUpRight size={12} />
            </RouterLink>
          </div>
          <div className="px-2 pb-2">
            {loading ? (
              <div className="px-3 py-3 space-y-3">
                <Skeleton className="h-12 w-full" rounded="rounded-xl" />
                <Skeleton className="h-12 w-full" rounded="rounded-xl" />
                <Skeleton className="h-12 w-full" rounded="rounded-xl" />
              </div>
            ) : topLinks.length === 0 ? (
              <EmptyState
                icon={<Link2 size={22} />}
                tone="brand"
                title="No links yet"
                description="Create your first link to see top performers here."
                action={
                  <RouterLink to={buildPath("links?new=1")}>
                    <Button icon={<Plus size={14} strokeWidth={2.5} />}>
                      Create a link
                    </Button>
                  </RouterLink>
                }
              />
            ) : (
              <div>
                {topLinks.map((link, idx) => (
                  <RouterLink
                    key={link._id}
                    to={buildPath(`analytics/${link._id}`)}
                    className="group flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-ink-50/80 transition-colors"
                  >
                    <div
                      className={`shrink-0 w-7 h-7 rounded-lg font-display font-bold text-[11px] flex items-center justify-center ${
                        idx === 0
                          ? "bg-brand-gradient text-white shadow-brand-glow"
                          : "bg-ink-100 text-ink-600"
                      }`}
                    >
                      {idx + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-ink-900 truncate">
                        {link.title || "Untitled"}
                      </div>
                      <div className="text-xs text-ink-500 truncate">
                        {link.url}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-semibold text-ink-900 text-sm tabular-nums">
                        {formatNumber(link.clicks)}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-ink-400">
                        clicks
                      </div>
                    </div>
                  </RouterLink>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent activity feed */}
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-5 pb-2 gap-3">
            <div>
              <h3 className="text-sm font-semibold text-ink-900 tracking-tight">
                Recent clicks
              </h3>
              <p className="text-xs text-ink-500 mt-0.5">
                Latest click events as they happen.
              </p>
            </div>
            <Badge tone="emerald" dot>
              Live
            </Badge>
          </div>
          <div className="px-2 pb-2">
            {loading ? (
              <div className="px-3 py-3 space-y-3">
                <Skeleton className="h-10 w-full" rounded="rounded-xl" />
                <Skeleton className="h-10 w-full" rounded="rounded-xl" />
                <Skeleton className="h-10 w-full" rounded="rounded-xl" />
              </div>
            ) : recentLogs.length === 0 ? (
              <EmptyState
                icon={<Activity size={22} />}
                tone="violet"
                title="No clicks yet"
                description="The first click will appear here in real time."
              />
            ) : (
              <div>
                {recentLogs.map((log) => (
                  <div
                    key={log._id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-ink-50/80 transition-colors"
                  >
                    <div className="shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-600 flex items-center justify-center">
                      <Activity size={13} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink-900 truncate">
                        <span className="font-medium">
                          {linkTitleById[log.url_tracker_id] || "Link"}
                        </span>{" "}
                        <span className="text-ink-500">·</span>{" "}
                        <span className="text-ink-500">
                          {[log.city, log.country].filter(Boolean).join(", ") ||
                            "Unknown location"}
                        </span>
                      </div>
                      <div className="text-[11px] text-ink-400 flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          {deviceIconFor(log.device_type)}
                          <span className="capitalize">
                            {log.device_type || "?"}
                          </span>
                        </span>
                        <span>·</span>
                        <span>{log.browser || "?"}</span>
                        <span>·</span>
                        <span>{formatDateTime(log.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
